import React, { useContext, useEffect, useMemo, useState } from "react";
import { UserContext } from "../UserContext";
import Header from "../Header";
import apiClient from "../../api/client";
import { toast } from "react-toastify";
import { HiOutlineRefresh } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import "./index.css";

/**
 * Deliberately simpler than InternetCustomerList — there's no "list all
 * customers" API on RailTel's side (only exact-username lookup), so
 * this reads from what's already stored via the link/unlink flow
 * (customer/railtel_link_account.php) instead of a live provider call.
 * That means no live status/online/package/expiry columns here — those
 * would mean one provider call per row just to render the list. Click
 * through to a customer's own RailTel tab for live details on that one
 * account.
 *
 * Also simpler on the navigation side: since this list already comes
 * from our own DB, user_id is right there in the response — no need
 * for InternetCustomerList's extra get_customer_by_mobile.php round
 * trip just to find out which internal customer a row corresponds to.
 */
const RailtelCustomersList = () => {

    const { token } = useContext(UserContext);

    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    const navigate = useNavigate();

    useEffect(() => {
        loadCustomers();
    }, []);

    const loadCustomers = async () => {
        setLoading(true);
        try {
            const { data } = await apiClient.get(
                "/customer/railtel_customer_list.php"
            );

            if (data.status === 200) {
                setCustomers(data.results || []);
            } else {
                toast.error(data.message || "Unable to load customers");
            }
        } catch (e) {
            toast.error("Network Error");
        }
        setLoading(false);
    };

    const filteredCustomers = useMemo(() => {
        return customers.filter(c => {
            const text = search.toLowerCase();
            return (
                (c.firstname + " " + c.lastname).toLowerCase().includes(text) ||
                (c.railtel_username || "").toLowerCase().includes(text) ||
                (c.mobile || "").includes(text) ||
                (c.operator_name || "").toLowerCase().includes(text)
            );
        });
    }, [customers, search]);

    const goToCustomer = (c) => {
        navigate(`/customer/${c.user_id}?tab=railtel`);
    };

    return (
        <>
            <Header />

            <div className="railtel-list-container">

                <div className="railtel-list-header">
                    <h2>🚄 RailTel Customers</h2>

                    <button className="refresh-btn" onClick={loadCustomers}>
                        Refresh
                    </button>
                    <button className="refresh-btn-mobile" onClick={loadCustomers}>
                        <HiOutlineRefresh />
                    </button>
                </div>

                <input
                    className="railtel-search"
                    placeholder="Search Customer, RailTel Username, Mobile, Operator..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />

                {loading ? (
                    <div className="skeleton-loader">
                        {[...Array(6)].map((_, index) => (
                            <div key={index} className="skeleton-row">
                                <div className="skeleton-cell"></div>
                                <div className="skeleton-cell"></div>
                                <div className="skeleton-cell"></div>
                                <div className="skeleton-cell"></div>
                                <div className="skeleton-cell"></div>
                                <div className="skeleton-cell"></div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="operator-table-wrapper">
                        <table className="operator-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Customer</th>
                                    <th>Mobile</th>
                                    <th>RailTel Username</th>
                                    <th>Operator</th>
                                    <th>Linked On</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCustomers.length === 0 && (
                                    <tr>
                                        <td colSpan="6">No Customers Found</td>
                                    </tr>
                                )}

                                {filteredCustomers.map((c, index) => (
                                    <tr
                                        key={c.link_id}
                                        onClick={() => goToCustomer(c)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <td>{index + 1}</td>
                                        <td>{c.firstname} {c.lastname}</td>
                                        <td>
                                            <span
                                                className="railtel-link"
                                                onClick={(e) => { e.stopPropagation(); goToCustomer(c); }}
                                            >
                                                {c.mobile}
                                            </span>
                                        </td>
                                        <td>
                                            <span
                                                className="railtel-link"
                                                onClick={(e) => { e.stopPropagation(); goToCustomer(c); }}
                                            >
                                                {c.railtel_username}
                                            </span>
                                        </td>
                                        <td>{c.operator_name}</td>
                                        <td>{c.linked_at}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

            </div>
        </>
    );
};

export default RailtelCustomersList;
